import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Pencil, Trash2, Newspaper } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyInput,
  TaskyLabel,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  useConfirm,
} from '../tasky';
import { useToast } from '../../contexts/ToastContext';
import {
  adminFetchNews,
  adminCreateNews,
  adminUpdateNews,
  adminDeleteNews,
  adminUploadNewsImage,
  adminUploadNewsVideo,
  NewsArticle,
  NewsCreateRequest,
  NewsUpdateRequest,
} from '../../services/api/newsService';
import RichTextEditor from './RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

interface NewsFormData {
  title: string;
  content: string;
  thumbnail_url: string;
  hero_video_url: string;
  is_published: boolean;
  show_as_popup: boolean;
}

const initialFormData: NewsFormData = {
  title: '',
  content: '',
  thumbnail_url: '',
  hero_video_url: '',
  is_published: false,
  show_as_popup: false,
};

const NewsManagementSection: React.FC = () => {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [formData, setFormData] = useState<NewsFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const [heroVideoUploading, setHeroVideoUploading] = useState(false);
  const heroVideoInputRef = useRef<HTMLInputElement>(null);

  // プレビューは公開後の描画と同じ経路を通すため、本文をサニタイズしてから
  // 流し込む（編集中に毎キーストロークで再計算しないようメモ化）。
  const sanitizedPreview = useMemo(
    () => sanitizeHtml(formData.content),
    [formData.content],
  );

  // サムネイル画像をアップロードして thumbnail_url にセットする
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setThumbnailUploading(true);
    try {
      const url = await adminUploadNewsImage(file);
      setFormData(prev => ({ ...prev, thumbnail_url: url }));
    } catch {
      setError('サムネイル画像のアップロードに失敗しました');
    } finally {
      setThumbnailUploading(false);
    }
  };

  // トップ動画をアップロードして hero_video_url にセットする（画像と同じ S3 経路）
  const handleHeroVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setHeroVideoUploading(true);
    try {
      const url = await adminUploadNewsVideo(file);
      setFormData(prev => ({ ...prev, hero_video_url: url }));
    } catch {
      setError('トップ動画のアップロードに失敗しました');
    } finally {
      setHeroVideoUploading(false);
    }
  };

  const fetchArticles = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminFetchNews(page);
      const articlesList = Array.isArray(response) ? response : (response.data || []);
      const totalLastPage = (response as any).last_page || 1;
      setArticles(articlesList);
      setLastPage(totalLastPage);
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError('ニュース一覧の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles(currentPage);
  }, [fetchArticles, currentPage]);

  const openCreateModal = () => {
    setSelectedArticle(null);
    setFormData(initialFormData);
    setIsModalOpen(true);
  };

  const openEditModal = (article: NewsArticle) => {
    setSelectedArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      thumbnail_url: article.thumbnail_url || '',
      hero_video_url: article.hero_video_url || '',
      is_published: article.is_published,
      show_as_popup: article.show_as_popup ?? false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('タイトルを入力してください', 'error');
      return;
    }
    if (!formData.content.trim()) {
      showToast('本文を入力してください', 'error');
      return;
    }

    setIsSaving(true);
    try {
      if (selectedArticle) {
        const updateData: NewsUpdateRequest = {
          title: formData.title,
          content: formData.content,
          thumbnail_url: formData.thumbnail_url || null,
          hero_video_url: formData.hero_video_url || null,
          is_published: formData.is_published,
          show_as_popup: formData.show_as_popup,
        };
        await adminUpdateNews(selectedArticle.id, updateData);
      } else {
        const createData: NewsCreateRequest = {
          title: formData.title,
          content: formData.content,
          thumbnail_url: formData.thumbnail_url || null,
          hero_video_url: formData.hero_video_url || null,
          is_published: formData.is_published,
          show_as_popup: formData.show_as_popup,
        };
        await adminCreateNews(createData);
      }
      closeModal();
      await fetchArticles(currentPage);
    } catch (err) {
      console.error('Failed to save news:', err);
      showToast('ニュース記事の保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (article: NewsArticle) => {
    if (!(await confirm({ title: `「${article.title}」を削除してもよろしいですか？`, tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      await adminDeleteNews(article.id);
      await fetchArticles(currentPage);
    } catch (err) {
      console.error('Failed to delete news:', err);
      showToast('ニュース記事の削除に失敗しました', 'error');
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-dark">ニュース管理</h2>
        <TaskyButton size="sm" onClick={openCreateModal}>
          <Plus />
          新規記事作成
        </TaskyButton>
      </div>

      {/* Error message */}
      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
          <TaskySpinner size="md" />
          読み込み中...
        </div>
      )}

      {/* Articles table */}
      {!isLoading && (
        <div>
          {articles.length > 0 ? (
            <>
              <TaskyTable>
                <TaskyTableHead>
                  <TaskyTableRow hoverable={false}>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>タイトル</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>公開状態</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>ポップアップ</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>公開日</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>作成日</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
                  </TaskyTableRow>
                </TaskyTableHead>
                <TaskyTableBody>
                  {articles.map((article) => (
                    <TaskyTableRow key={article.id}>
                      <TaskyTableCell className="text-gray-400">{article.id}</TaskyTableCell>
                      <TaskyTableCell className="font-medium">
                        {article.title.length > 50
                          ? article.title.substring(0, 50) + '...'
                          : article.title}
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyBadge variant={article.is_published ? 'success' : 'warning'} dot>
                          {article.is_published ? '公開中' : '下書き'}
                        </TaskyBadge>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        {article.show_as_popup ? (
                          <TaskyBadge variant="info">ON</TaskyBadge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TaskyTableCell>
                      <TaskyTableCell className="text-gray-500">{formatDate(article.published_at)}</TaskyTableCell>
                      <TaskyTableCell className="text-gray-500">{formatDate(article.created_at)}</TaskyTableCell>
                      <TaskyTableCell>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="編集"
                            onClick={() => openEditModal(article)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-warm-hover hover:text-gray-600 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="削除"
                            onClick={() => handleDelete(article)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-warm-hover hover:text-destructive transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>

              {/* Pagination */}
              {lastPage > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-tasky-sm text-sm bg-white border border-warm-border text-dark hover:bg-warm-hover transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
                  >
                    前へ
                  </button>
                  <span className="px-3 text-sm text-gray-500">
                    {currentPage} / {lastPage}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.min(lastPage, prev + 1))}
                    disabled={currentPage === lastPage}
                    className="px-4 py-2 rounded-tasky-sm text-sm bg-white border border-warm-border text-dark hover:bg-warm-hover transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
                  >
                    次へ
                  </button>
                </div>
              )}
            </>
          ) : (
            <TaskyEmptyState icon={<Newspaper />} title="ニュース記事がまだありません" />
          )}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
          <div className="bg-white rounded-tasky-lg w-full max-w-[720px] max-h-[90vh] overflow-auto p-8 shadow-card">
            <h3 className="text-lg font-semibold text-dark mb-6">
              {selectedArticle ? 'ニュース記事を編集' : '新規ニュース記事作成'}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div className="mb-5">
                <TaskyLabel required>タイトル</TaskyLabel>
                <TaskyInput
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="記事のタイトルを入力してください"
                  required
                />
              </div>

              {/* Thumbnail（アップロード or URL） */}
              <div className="mb-5">
                <TaskyLabel>サムネイル画像（任意）</TaskyLabel>
                <div className="flex items-start gap-2.5">
                  <TaskyInput
                    type="url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                    placeholder="画像をアップロード、または URL を入力"
                    className="flex-1"
                  />
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleThumbnailUpload}
                  />
                  <TaskyButton
                    type="button"
                    variant="secondary"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={thumbnailUploading}
                    className="whitespace-nowrap text-sm"
                  >
                    {thumbnailUploading ? 'アップロード中…' : '画像を選択'}
                  </TaskyButton>
                </div>
                {formData.thumbnail_url && (
                  <img
                    src={formData.thumbnail_url}
                    alt="サムネイルプレビュー"
                    className="mt-2.5 max-h-40 rounded-tasky-sm border border-warm-border object-contain"
                  />
                )}
              </div>

              {/* Hero video（アップロード or URL）。ポップアップ／詳細のトップで自動再生する。 */}
              <div className="mb-5">
                <TaskyLabel>トップ動画（任意）</TaskyLabel>
                <div className="flex items-start gap-2.5">
                  <TaskyInput
                    type="url"
                    value={formData.hero_video_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, hero_video_url: e.target.value }))}
                    placeholder="動画をアップロード、または URL を入力"
                    className="flex-1"
                  />
                  <input
                    ref={heroVideoInputRef}
                    data-testid="news-hero-video-input"
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    onChange={handleHeroVideoUpload}
                  />
                  <TaskyButton
                    type="button"
                    variant="secondary"
                    onClick={() => heroVideoInputRef.current?.click()}
                    disabled={heroVideoUploading}
                    className="whitespace-nowrap text-sm"
                  >
                    {heroVideoUploading ? 'アップロード中…' : '動画を選択'}
                  </TaskyButton>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  mp4 / webm / mov（50MB まで）。設定すると、サムネイル画像の代わりに自動再生（ミュート）で表示されます。
                </p>
                {formData.hero_video_url && (
                  <div className="mt-2.5">
                    {/* 管理者がアップロード結果を確認するためのプレビュー（自動再生はしない） */}
                    <video
                      data-testid="news-hero-video-preview"
                      src={formData.hero_video_url}
                      controls
                      muted
                      preload="metadata"
                      className="max-h-40 w-full rounded-tasky-sm border border-warm-border bg-black object-contain"
                    />
                    <TaskyButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, hero_video_url: '' }))}
                      className="mt-1.5"
                    >
                      動画を削除
                    </TaskyButton>
                  </div>
                )}
              </div>

              {/* Content - リッチテキストエディタ（ツールバーで画像・リンク挿入） */}
              <div className="mb-5">
                <TaskyLabel required>本文</TaskyLabel>
                <p className="text-xs text-gray-400 mb-1.5">
                  ツールバーから太字・見出し・箇条書き・リンク・画像を挿入できます。Enter で改行されます。
                </p>
                <RichTextEditor
                  value={formData.content}
                  onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
                  onImageUpload={adminUploadNewsImage}
                />
              </div>

              {/* Preview */}
              {formData.content && (
                <div className="mb-5">
                  <TaskyLabel>プレビュー</TaskyLabel>
                  <div
                    className="p-4 border border-warm-border rounded-tasky-sm bg-warm-bg max-h-[300px] overflow-auto text-sm leading-relaxed text-gray-700"
                    dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
                  />
                </div>
              )}

              {/* Published toggle */}
              <div className="mb-7">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div className="relative inline-block">
                    <input
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                      className="absolute w-0 h-0 opacity-0"
                    />
                    <div
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                        formData.is_published ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${
                          formData.is_published ? 'left-[22px]' : 'left-0.5'
                        }`}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-dark">
                    {formData.is_published ? '公開する' : '下書き保存'}
                  </span>
                </label>
                <p className="text-xs text-gray-400 mt-1 ml-[54px]">
                  {formData.is_published ? 'ユーザーに公開されます' : '下書きとして保存され、ユーザーには表示されません'}
                </p>
              </div>

              {/* Popup toggle */}
              <div className="mb-7">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div className="relative inline-block">
                    <input
                      type="checkbox"
                      checked={formData.show_as_popup}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_as_popup: e.target.checked }))}
                      className="absolute w-0 h-0 opacity-0"
                    />
                    <div
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                        formData.show_as_popup ? 'bg-success' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${
                          formData.show_as_popup ? 'left-[22px]' : 'left-0.5'
                        }`}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-dark">
                    {formData.show_as_popup ? 'ポップアップ通知する' : 'ポップアップ通知しない'}
                  </span>
                </label>
                <p className="text-xs text-gray-400 mt-1 ml-[54px]">
                  {formData.show_as_popup
                    ? '公開中の場合、ログイン後ホームでポップアップ表示されます（全ユーザー対象）'
                    : 'ホームのポップアップには表示されません'}
                </p>
              </div>

              {/* Form actions */}
              <div className="flex justify-end gap-3">
                <TaskyButton type="button" variant="secondary" onClick={closeModal} disabled={isSaving}>
                  キャンセル
                </TaskyButton>
                <TaskyButton type="submit" isLoading={isSaving}>
                  {isSaving ? '保存中...' : selectedArticle ? '更新する' : '作成する'}
                </TaskyButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default NewsManagementSection;
